import type { components } from '@fluencelabs/vodopad-admin-api'

import { isInternalPricingGroup } from '../pricingGroups'

type Cluster = components['schemas']['ClusterCapacityDto']
type IpFamily = components['schemas']['IpAddressType']
type StorageKind = components['schemas']['CapacityStorageType']

const NANOCORES = 1e9
const GB = 1e9

/** Measured hardware next to sold and internal amounts from the database. */
export interface CapacityRow {
  resource: string
  total: number
  allocatable: number
  sold: number
  internal: number
  platformPods?: number
  /** What can still be reserved: disks are thin, so it subtracts provisioned volume sizes, not written bytes. */
  free: number
  /** Disks only: bytes not yet written, including space provisioned volumes may still fill. */
  physicalFree?: number
  /** What sold and internal leave out, when they are known to be incomplete. */
  note?: string
}

export interface IpRow {
  family: IpFamily
  total: bigint
  sold: bigint
  platform: bigint
  free: bigint
}

const sum = <T>(items: T[], value: (item: T) => number) =>
  items.reduce((total, item) => total + value(item), 0)

function splitByAudience<T extends { pricingGroup: string }>(
  rows: T[],
  value: (row: T) => number,
) {
  const internal = rows.filter((row) =>
    isInternalPricingGroup(row.pricingGroup),
  )
  const sold = rows.filter((row) => !isInternalPricingGroup(row.pricingGroup))
  return { sold: sum(sold, value), internal: sum(internal, value) }
}

export function computeRows(cluster: Cluster): CapacityRow[] {
  const nodes = cluster.inventory?.nodes ?? []
  const { vms, storage } = cluster.recordedProductCommitments
  const nonTenant = (
    requests?: components['schemas']['SchedulerRequests'] | null,
  ) =>
    requests
      ? {
          cpu:
            (requests.platformReserved.cpuNanocores +
              requests.unattributedReserved.cpuNanocores) /
            NANOCORES,
          memory:
            (requests.platformReserved.memoryBytes +
              requests.unattributedReserved.memoryBytes) /
            GB,
        }
      : { cpu: 0, memory: 0 }
  const cpu = splitByAudience(vms, (vm) => vm.requestedCpu ?? 0)
  const ram = splitByAudience(vms, (vm) => vm.ramGb)
  const unpriced = sum(
    vms.filter((vm) => vm.requestedCpu == null),
    (vm) => vm.vmCount,
  )
  const rows: CapacityRow[] = [
    {
      resource: 'CPU (cores)',
      total: sum(nodes, (n) => n.cpu.total),
      allocatable: sum(nodes, (n) => n.cpu.allocatable),
      ...cpu,
      platformPods: sum(nodes, (n) => nonTenant(n.schedulerRequests).cpu),
      free: sum(nodes, (n) => n.cpu.free),
      note:
        unpriced > 0
          ? `${unpriced} shared VMs are not counted: the cluster has no CPU allocation ratio`
          : undefined,
    },
    {
      resource: 'RAM (GB)',
      total: sum(nodes, (n) => n.memory.totalBytes) / GB,
      allocatable: sum(nodes, (n) => n.memory.allocatableBytes) / GB,
      ...ram,
      platformPods: sum(nodes, (n) => nonTenant(n.schedulerRequests).memory),
      free: sum(nodes, (n) => n.memory.freeBytes) / GB,
    },
  ]
  const kinds: [StorageKind, boolean, string][] = [
    ['nvme', false, 'NVMe storage (GB)'],
    ['replicatedNvme', true, 'Replicated NVMe storage (GB, raw)'],
  ]
  for (const [kind, replicated, label] of kinds) {
    const pools = nodes.flatMap((n) =>
      n.storageByType.filter((s) => s.storageType === kind),
    )
    if (pools.length === 0) continue
    // Pools report raw replica bytes; recorded volumes are logical, so replicate them to compare.
    const factor = replicated
      ? pools.find((s) => s.reservations)?.reservations?.replicationFactor
      : 1
    const logical = splitByAudience(
      storage.filter(
        (s) => s.storageType === 'NVMe' && s.replicated === replicated,
      ),
      (s) => (Number(s.logicalBytes) * (factor ?? 1)) / GB,
    )
    rows.push({
      resource: label,
      total: sum(pools, (s) => s.totalBytes) / GB,
      allocatable: sum(pools, (s) => s.totalBytes) / GB,
      ...logical,
      free: sum(pools, (s) => Math.max(s.totalBytes - s.claimedBytes, 0)) / GB,
      physicalFree: sum(pools, (s) => s.freeBytes) / GB,
      note:
        factor === undefined
          ? 'Replication factor not measured: sold is logical, not raw'
          : undefined,
    })
  }
  return rows
}

export function computeIpRows(cluster: Cluster): IpRow[] {
  const pools = cluster.inventory?.publicIps
  if (!pools) return []
  const families: [IpFamily, components['schemas']['IpPoolCounts']][] = [
    ['V4', pools.ipv4],
    ['V6', pools.ipv6],
  ]
  return families.map(([family, pool]) => {
    const internal = BigInt(
      sum(
        cluster.recordedProductCommitments.publicIps.filter(
          (ip) =>
            ip.addressType === family &&
            isInternalPricingGroup(ip.pricingGroup),
        ),
        (ip) => ip.assignedAddressCount,
      ),
    )
    const claimed = BigInt(pool.claimed)
    const sold = claimed > internal ? claimed - internal : 0n
    return {
      family,
      total: BigInt(pool.total),
      sold,
      platform: BigInt(pool.otherClaimed) + (claimed - sold),
      free: BigInt(pool.free),
    }
  })
}

/** Share of what can be scheduled that customers hold. */
export function soldShare(row: CapacityRow): number | undefined {
  return row.allocatable > 0 ? row.sold / row.allocatable : undefined
}
